import { Menu, StepBack, User, X } from 'lucide-react'
import { useContext, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import logo from '../assets/logo.png'
import { AuthContext, useAuth } from '../contexts/AuthContext'
import { Book, Mic, Layout, LogOut, LeafIcon, LucideWholeWord, LogIn, CalendarHeart, MessageCircle, Award, Trophy, Puzzle, Target, Gamepad2, Flame, Frown, Search, Brain, Quote, Users, Swords, ChevronLeft } from 'lucide-react';
import Modal from './Modal'
import { fetchCurrentUserLeaderboardEntry, fetchCrimeWeekActivity } from '../services/crimeGameService';
import { GameLeaderboardEntry } from '../types/crime-game';
import { StreakModal } from './gamification/StreakModal';
import NotificationCenter from './social/NotificationCenter';
import { useEffect } from 'react';
import * as vocabularyService from '../services/vocabularyService';

function NavbarLanding({ isLanding }) {
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
    const navigate = useNavigate()
    const location = useLocation();
    const { currentUser, logout } = useAuth();
    const [logoutModal, setLogoutModal] = useState(false);
    const [streakModalOpen, setStreakModalOpen] = useState(false);
    const [userStats, setUserStats] = useState<GameLeaderboardEntry | null>(null);
    const [crimeWeekHistory, setCrimeWeekHistory] = useState<boolean[] | undefined>(undefined);
    const [vocabStats, setVocabStats] = useState<{ trainingStreak: number; wordDayStreak: number; trainingHistory: boolean[]; wordDayHistory: boolean[] } | null>(null);
    const streakButtonRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchStats = async () => {
            if (currentUser) {
                try {
                    // Fetch crime game stats
                    const crimeStats = await fetchCurrentUserLeaderboardEntry(currentUser.uid, 'global');
                    setUserStats(crimeStats);
                    
                    // Fetch crime game week activity
                    const crimeHistory = await fetchCrimeWeekActivity(currentUser.uid);
                    setCrimeWeekHistory(crimeHistory);
                    
                    // Fetch vocabulary stats (training and word of the day)
                    const vocabUserStats = await vocabularyService.getUserStats(currentUser.uid);
                    const trainingHistory = vocabularyService.getTrainingWeekActivity(vocabUserStats.dailyPoints || {}, currentUser.uid);
                    const wordDayHistory = await vocabularyService.fetchWordDayWeekActivity(currentUser.uid);
                    
                    console.log('NavbarLanding - Fetched vocab stats:', vocabUserStats);
                    console.log('NavbarLanding - wordDayStreak:', vocabUserStats.wordDayStreak);
                    
                    setVocabStats({
                        trainingStreak: vocabUserStats.streakCount || 0,
                        wordDayStreak: vocabUserStats.wordDayStreak || 0,
                        trainingHistory,
                        wordDayHistory
                    });
                } catch (e) {
                    console.error("Failed to fetch streak stats", e);
                }
            }
        };
        fetchStats();
        
        // Listen for streak updates
        const handleWordDayStreakUpdate = () => {
            // Add a delay to ensure database write has completed
            setTimeout(() => {
                console.log('NavbarLanding - wordDayStreakUpdated event received, refreshing stats...');
                fetchStats();
            }, 1000);
        };
        
        const handleCrimeGameStreakUpdate = () => {
            // Add a delay to ensure database write has completed
            setTimeout(() => {
                console.log('NavbarLanding - crimeGameStreakUpdated event received, refreshing stats...');
                fetchStats();
            }, 1000);
        };
        
        window.addEventListener('wordDayStreakUpdated', handleWordDayStreakUpdate);
        window.addEventListener('crimeGameStreakUpdated', handleCrimeGameStreakUpdate);
        
        // Refresh when page becomes visible (user switches tabs/windows)
        const handleVisibilityChange = () => {
            if (!document.hidden && currentUser) {
                console.log('NavbarLanding - Page visible, refreshing stats...');
                fetchStats();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            window.removeEventListener('wordDayStreakUpdated', handleWordDayStreakUpdate);
            window.removeEventListener('crimeGameStreakUpdated', handleCrimeGameStreakUpdate);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [currentUser]);

    const toggleNavbar = () => {
        setMobileDrawerOpen(!mobileDrawerOpen)
    }

      const goLogin = () => {
        toggleNavbar()
        navigate('/login')
      }

      const goSignUp = () => {
        toggleNavbar()
        navigate('/signup')
      }

      const handleLogout = () => {
        toggleNavbar()
        setLogoutModal(false)
        logout()
      }

      const goBack = () => {
        if (mobileDrawerOpen) toggleNavbar();
        
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
        } else {
            navigate('/dashboard');
        }
      }

    return (
        <nav className={`sticky top-0 z-[60] py-1 backdrop-blur-lg border-b border-gray-200 w-full bg-white transition-all shadow-sm`}>
            <Modal 
                isOpen={logoutModal} 
                setIsModalOpen={setLogoutModal} 
                title='Logout'
                message='Do you want to logout?'
                actionText='Logout'
                action={handleLogout}
                />
            <StreakModal
                isOpen={streakModalOpen}
                onClose={() => setStreakModalOpen(false)}
                crimeStreak={userStats?.dailyStreak || 0}
                trainingStreak={vocabStats?.trainingStreak || 0}
                wordDayStreak={vocabStats?.wordDayStreak || 0}
                crimeHistory={crimeWeekHistory}
                trainingHistory={vocabStats?.trainingHistory}
                wordDayHistory={vocabStats?.wordDayHistory}
                buttonRef={streakButtonRef}
            />
            <div className="w-full px-4 md:px-6 relative text-sm">
                <div className="flex flex-row items-center">
                    <div className="flex items-center flex-1 min-w-0">
                        {!isLanding && location.pathname !== '/dashboard' && location.pathname !== '/' && (
                            <button 
                                onClick={goBack}
                                className="mr-2 md:mr-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                aria-label="Go back"
                            >
                                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
                            </button>
                        )}
                        <div
                            className="flex items-center cursor-pointer min-w-0"
                            onClick={() => isLanding ? navigate('/') : navigate('/dashboard')}
                        >
                            <img referrerPolicy="no-referrer" src={logo} alt="logo" className="h-16 w-auto md:h-20 md:w-auto mr-1.5 md:mr-3 flex-shrink-0" />
                            <span className="hidden md:block text-xl md:text-3xl tracking-tight font-semibold truncate">LinguaLearn</span>
                            {/* Show page title or breadcrumb on desktop when logged in instead of Logo? Or keep Logo? */}
                            {/* Keeping Logo hidden on desktop if logged in to avoid double logo (sidebar has one), or keep consistent? */}
                            {/* Sidebar has Logo. Let's hide this logo on desktop if !isLanding to avoid duplication near sidebar. */}
                        </div>
                    </div>

                    {/* Notifications (Visible if logged in) */}
                    {currentUser && <NotificationCenter />}

                    {/* Streak Badge (Visible on Desktop & Mobile if logged in) */}
                    {currentUser && (
                        <div
                            ref={streakButtonRef}
                            onClick={() => setStreakModalOpen(true)}
                            className="mr-2 md:mr-4 cursor-pointer relative flex-shrink-0"
                        >
                            {(() => {
                                const wordDayStreak = vocabStats?.wordDayStreak || 0;
                                const crimeStreak = userStats?.dailyStreak || 0;
                                const trainingStreak = vocabStats?.trainingStreak || 0;
                                
                                return (
                                    <div className="relative inline-block rounded-full p-[3px] overflow-hidden">
                                        {/* Rotating border - only this rotates */}
                                        <div className="absolute inset-0 rounded-full animate-rotate-border" 
                                             style={{
                                                 background: 'conic-gradient(from 0deg, #fbbf24 0deg, #c084fc 120deg, #f9a8d4 240deg, #fbbf24 360deg)'
                                             }}></div>
                                        
                                        {/* Badge content - static */}
                                        <div className="relative flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-full bg-white shadow-md z-10">
                                            {/* Icons with counts */}
                                            <div className="flex items-center gap-1 md:gap-1.5">
                                                {/* Crime Game */}
                                                <div className="flex items-center gap-0.5 md:gap-1" title={`Crime Game: ${crimeStreak} days`}>
                                                    <Search className={`w-3 h-3 md:w-3.5 md:h-3.5 ${crimeStreak > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
                                                    <span className={`text-[10px] md:text-xs font-bold ${crimeStreak > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                                                        {crimeStreak}
                                                    </span>
                                                </div>
                                                
                                                {/* Divider */}
                                                <div className="w-px h-3 md:h-4 bg-gray-300"></div>
                                                
                                                {/* Daily Training */}
                                                <div className="flex items-center gap-0.5 md:gap-1" title={`Daily Training: ${trainingStreak} days`}>
                                                    <Brain className={`w-3 h-3 md:w-3.5 md:h-3.5 ${trainingStreak > 0 ? 'text-purple-500' : 'text-gray-400'}`} />
                                                    <span className={`text-[10px] md:text-xs font-bold ${trainingStreak > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                                                        {trainingStreak}
                                                    </span>
                                                </div>
                                                
                                                {/* Divider */}
                                                <div className="w-px h-3 md:h-4 bg-gray-300"></div>
                                                
                                                {/* Word of the Day */}
                                                <div className="flex items-center gap-0.5 md:gap-1" title={`Word of the Day: ${wordDayStreak} days`}>
                                                    <CalendarHeart className={`w-3 h-3 md:w-3.5 md:h-3.5 ${wordDayStreak > 0 ? 'text-pink-500' : 'text-gray-400'}`} />
                                                    <span className={`text-[10px] md:text-xs font-bold ${wordDayStreak > 0 ? 'text-pink-600' : 'text-gray-400'}`}>
                                                        {wordDayStreak}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* <ul className="hidden lg:flex ml-14 space-x-12">
                        {navItems.map((item, index) => (
                            <li key={index}>
                                <a href={item.href} className="text-black">
                                    {item.label}
                                </a>
                            </li>
                        ))}
                    </ul> */}
                    <div className="hidden lg:flex justify-center space-x-4 items-center">
                        {!currentUser ? (
                            <>
                                <a onClick={() => navigate('/login')} className="py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors font-medium">
                                    Entrar
                                </a>
                                <a
                                    className="text-white bg-gradient-to-r from-purple-500 to-purple-800 font-semibold py-2 px-4 rounded-md cursor-pointer hover:from-purple-600 hover:to-purple-900 transition-all shadow-sm hover:shadow-md"
                                    onClick={() => navigate('/signup')}
                                >
                                    Criar conta
                                </a>
                            </>
                        ) : (
                            <button
                                onClick={() => setLogoutModal(true)}
                                className="flex items-center gap-2 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors font-medium"
                            >
                                <LogOut className="w-4 h-4" />
                                Sair
                            </button>
                        )}
                    </div>
                    <div className="lg:hidden md:flex flex-col justify-end">
                        <button onClick={toggleNavbar}>{mobileDrawerOpen ? <X /> : <Menu />}</button>
                    </div>
                </div>
                {mobileDrawerOpen && (
                    <div className="absolute left-0 right-0 top-full z-20 bg-gray-200 w-full flex flex-col justify-start items-center lg:hidden max-h-[calc(100vh-80px)] overflow-y-auto shadow-lg pb-4">
                        {/* <ul>
                            {navItems.map((item, index) => (
                                <li className="py-4" key={index}>
                                    <a className="text-white" href={item.href}>
                                        {item.label}
                                    </a>
                                </li>
                            ))}
                        </ul> */}
                        <div className="pt-2 pb-2 flex flex-col justify-between gap-2 items-start w-full bg-white px-4">
                            <div className="grid grid-cols-1 w-full gap-2">
                                <a onClick={() => { toggleNavbar(); navigate('/dashboard'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Layout className='mr-2' />
                                    Dashboard
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/vocabulary'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Book className='mr-2' />
                                    Vocabulary
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/expressions'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Quote className='mr-2' />
                                    Expressions
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/themes'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <LeafIcon className='mr-2' />
                                    Themes
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/speaking'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Mic className='mr-2' />
                                    Speaking
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/chat'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <MessageCircle className='mr-2' />
                                    AI Chat
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/translate'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <LucideWholeWord className='mr-2' />
                                    Translate
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/day'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <CalendarHeart className='mr-2' />
                                    Day Word
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/training'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Target className='mr-2' />
                                    Training
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/crime-game'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Gamepad2 className='mr-2' />
                                    Crime Game
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/memory-game'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Puzzle className='mr-2' />
                                    Jogo da Memória
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/ranking'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Award className='mr-2' />
                                    Ranking
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/achievements'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Trophy className='mr-2' />
                                    Achievements
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/social'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Users className='mr-2' />
                                    Friends
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/challenges'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <Swords className='mr-2' />
                                    Challenges
                                </a>
                                <a onClick={() => { toggleNavbar(); navigate('/profile'); }} className="flex items-center rounded-md px-4 py-2 text-base bg-white w-full border border-gray-300 shadow-sm hover:bg-blue-50">
                                    <User className='mr-2' />
                                    Profile
                                </a>
                            </div>
                            { (!currentUser || isLanding) && (<a onClick={() => goLogin()} className="flex rounded-full border px-16 py-2 text-base bg-white w-full capitalize border-gray-400 shadow-lg hover:bg-blue-400 hover:text-white">
                               <LogIn className='mr-2' />
                               Entrar
                            </a>)}
                            { !currentUser && (<a onClick={() => goSignUp()} className="flex rounded-full px-16 py-2 border text-base bg-white w-full capitalize border-gray-400 shadow-lg hover:bg-blue-400 hover:text-white">
                                <User className='mr-2' />
                                Criar Conta
                            </a>)}
                            { !isLanding && currentUser && (<a onClick={() => setLogoutModal(true)} className="flex rounded-full px-16 py-2 border text-base bg-white w-full capitalize border-gray-400 shadow-lg hover:bg-blue-400 hover:text-white">
                                <LogOut className='mr-2' />
                                Sair
                            </a>)}
                            {location.pathname !== '/' && location.pathname !== '/dashboard' && !isLanding && (<a onClick={() => goBack()} className="flex rounded-full px-16 py-2 border text-base bg-white w-full capitalize border-gray-400 shadow-lg hover:bg-blue-400 hover:text-white cursor-pointer">
                                <StepBack className='mr-2' />
                                Voltar
                            </a>)}
                        </div>
                    </div>
                )}
            </div>
        </nav>
    )
}

export default NavbarLanding
